import "./style.scss";

import { Breadcrumbs, CrumbClickFunc } from "@amzn/stencil-react-components/breadcrumbs";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor, ChipIconPosition, ChipSize } from "@amzn/stencil-react-components/chip";
import { Col, Container, Row, View } from "@amzn/stencil-react-components/layout";
import { Hr } from "@amzn/stencil-react-components/layout";
import { WithModal } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { Text } from "@amzn/stencil-react-components/text";
import {
  IconAccessibilityPersonCircleExtraSmall,
  IconBookmarkFillExtraSmall,
  IconDesktopExtraSmall,
  IconMessageSmall,
} from "@amzn/stencil-react-icons";
import { IconSize } from "@amzn/stencil-react-icons/icons/interfaces";
import { checkOsCompatibility, StringHelper } from "common/helpers";
import { Paths } from "common/paths/paths";
import { PreCheckoutInformation } from "common/types";
import { useAssetReturns } from "hooks";
import parse from "html-react-parser";
import NoLogoImage from "img/no_logo_image.png";
import { getUserOS } from "lib/helpers";
import { AppSenseService, CheckLicenseCapOutput } from "lib/services/app-sense-service";
import { FeedbackModelRecord } from "lib/services/catalogical-models";
import { IItemVersion, ItemDetails, ItemSubtypes, ItemTypes } from "lib/types/items";
import { capitalize, forEach } from "lodash";
import moment from "moment";
import React, { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { CheckoutState } from "view/Page/CheckoutPage/CheckoutPage";

import { getString } from "../../uistringlabels/uiStringUtils";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
} from "../CustomerFeedback";
import { EtaStatusIndicator } from "../EtaStatusIndicator/EtaStatusIndicator";
import { FoundationalChip } from "../FoundationalChip";
import licenses from "../LicenseSelect/figma-licenses.json";
import { License, LicenseSelect } from "../LicenseSelect/LicenseSelect";
import { OrderNow } from "../OrderNow";
import { PageMainContainer } from "../PageMainContainer/PageMainContainer";
import { ProductDetailsBanners } from "../ProductDetailsBanner";
import { StarAverageRating, StarAverageRatingSize } from "../Ratings";
import { SelectChildSku } from "../SelectChildSku";
import { SubmitOrderModal } from "../SubmitOrderModal";
import { SubmitReturnModal } from "../SubmitReturnModal";
import { SubmitReviewForm } from "../SubmitReviewForm";
import { useProductDetailsPage } from "./useProductDetailsPage";

// Constant to validate if the PDP is for a figma product
const FIGMA = "figma";

// Tab titles
const tabs = ["About the product", "Resources", "Support"];

const breadCrumbsMapping: { [key in ItemTypes]: string } = {
  [ItemTypes.SOFTWARE]: "/",
  [ItemTypes.HARDWARE]: Paths.hardwareSearch.url,
};

export interface ProductDetailsPageProps {
  /**
   * If it is a software or hardware item, determines OrderNow button behavior
   */
  assetType: ItemTypes;

  /**
   * Item to render
   */
  item: ItemDetails;

  /**
   * Same as parent item info if the item does not have children or versions
   */
  defaultVersion: IItemVersion | undefined;

  /**
   * Empty if the item does not have children or versions
   */
  versions: IItemVersion[];

  /**
   * Updates the default version used to make the order
   * @param version The selected version in the radio/select
   */
  updateVersionSelected: (version: any) => void;
  preCheckoutInformation?: PreCheckoutInformation;
  fetchPreCheckoutData?: (token: string) => void;
  userVote?: FeedbackModelRecord;
}

export const ProductDetailsPage: FunctionComponent<ProductDetailsPageProps> = (props: ProductDetailsPageProps) => {
  const appSenseService = new AppSenseService();
  const userOS: string[] = getUserOS();
  const history = useHistory();

  const isOsCompatible = checkOsCompatibility(
    props.item.os ?? [""],
    userOS,
    props.assetType,
    props.item.subtype as ItemSubtypes | undefined
  );
  const [selectedVersion, setSelectedVersion] = useState(props.defaultVersion ?? undefined);
  const [isPriceOverwritten, setIsPriceOverwritten] = useState<boolean>(props.item.taxonomyId!.includes(FIGMA));
  const [amount, setAmount] = useState(isPriceOverwritten ? licenses[0].price : props.defaultVersion?.amount);
  const [licenseSelection, setLicenseSelection] = useState<License | null>(isPriceOverwritten ? licenses[0] : null);
  const [limitCap, setLimitCap] = useState<CheckLicenseCapOutput>({} as CheckLicenseCapOutput);
  const [hasOrderedItem, setHasOrderedItem] = useState<string | undefined>();
  const [isLicenseCapLoading, setIsLicenseCapLoading] = useState<boolean>(false);
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });
  const [openFlyout, setOpenFlyout] = useState(false);

  const { order, activeOrder, loadingOrders } = useProductDetailsPage(props.item.taxonomyId, selectedVersion);

  const isFoundational = props.item.itptClassification == "Foundational";

  const {
    canReturn,
    returnAsset,
    isLoading: returnLoading,
    error: returnError,
    hasLicense,
    returnStatus,
    inProgress,
    returnRequests,
    checkAssetStatus,
  } = useAssetReturns({
    itemId: selectedVersion?.taxonomyId || props.item.taxonomyId,
    productName: selectedVersion?.applinkProductName || props.item.applinkProductName,
    instanceName: selectedVersion?.applinkInstanceName || props.item.applinkInstanceName,
    skipValidation: !(selectedVersion?.isReturnable ?? props.item.isReturnable),
  });

  const assetStatus = {
    hasLicense,
    returnStatus: {
      inProgress,
      returnRequests,
    },
  };

  const isLoadingAssetReturnStatus = returnLoading;

  useEffect(() => {
    if (props.assetType === ItemTypes.HARDWARE) {
      handleChangeVersion(props.versions[0]);
    }
  }, []);

  useEffect(() => {
    checkPreviouslyOrder();
  }, [order]);

  useEffect(() => {
    if (!isPriceOverwritten) {
      setAmount(props.defaultVersion?.amount);
    }
  }, [props.defaultVersion]);

  useEffect(() => {
    checkLicenseCap(props.item.taxonomyId!, !props.item.requiresLicenseCapValidation!);
  }, [props.item.taxonomyId]);

  const checkPreviouslyOrder = async () => {
    if (order) {
      setHasOrderedItem(moment(order.requested?.on?.toString()).format("MM/DD/YYYY"));
    } else {
      setHasOrderedItem(undefined);
    }
  };

  const checkLicenseCap = async (taxonomyId: string, shouldSkipValidation: boolean) => {
    setIsLicenseCapLoading(true);
    try {
      const result: CheckLicenseCapOutput = await appSenseService.checkLicenseCap(taxonomyId, shouldSkipValidation);
      setLimitCap(result);
    } catch (error) {
      /**
       * possible errors:
       * 1. user alias not registered in AppSense table
       */
      // default values
      setLimitCap({
        isCapReached: false,
        closestLeaderWithExceededLimit: "",
      });
    } finally {
      setIsLicenseCapLoading(false);
    }
  };

  // This useEffect was moved up to be with the other useEffect hooks

  /* istanbul ignore next */
  const handleCrumbClick: CrumbClickFunc = (event, crumb) => {
    event.preventDefault();
    if (crumb.url) {
      history.push(crumb.url);
    }
  };

  /* istanbul ignore next */
  const handleChangeVersion = (newSelectedSku: any) => {
    setSelectedVersion(newSelectedSku);
    props.updateVersionSelected(newSelectedSku);
  };

  /**
   * Updates the selected license and related state values.
   *
   * @param {License | undefined} newLicense - The license to select, or undefined
   * @description When a license is provided, updates the amount to the license price,
   * marks the price as manually overwritten, and updates the license selection state.
   */
  const updateSelectedLicense = (newLicense?: License) => {
    if (newLicense) {
      setAmount(newLicense.price);
      setIsPriceOverwritten(true);
      setLicenseSelection(newLicense);
    }
  };

  if (!props.item) return <div>No item data</div>;

  const renderModal = ({ close }: { close: () => void }) => {
    return (
      <SubmitOrderModal
        close={close}
        preCheckoutData={props.preCheckoutInformation}
        fetchPreCheckoutData={props.fetchPreCheckoutData!}
        taxonomyId={props.item.taxonomyId!}
        name={props.item.name!}
        status={props.item.status}
        etaDays={props.item.etaDays}
        hasStock={props.item.hasStock}
        externalButtonText={props.item.externalButtonText}
        orderRedirectURL={props.item.orderRedirectUrl}
        orderWorkflow={props.item.orderWorkflow ?? ""}
        license={licenseSelection ?? undefined}
        isOsCompatible={isOsCompatible}
      />
    );
  };

  const renderFlyout = () => {
    return (
      <SubmitReviewForm
        productName={props.item.parentName ?? props.item.name}
        titleText={"Submit review"}
        isOpen={openFlyout}
        setIsOpen={setOpenFlyout}
        previousRating={props.userVote?.rating}
      />
    );
  };

  /* istanbul ignore next */
  const goToCheckoutPage = () => {
    const state = {
      parentName: props.item.parentName ?? props.item.name,
      parentTaxonomyId: props.item.parentTxId,
      defaultVersion: props.defaultVersion,
      versions: props.versions,
    } as CheckoutState;

    history.push({
      pathname: Paths.checkout.url,
      state,
    });
  };

  const displayListOfStrings = (strings: string[]) => {
    return strings.map((string) => StringHelper.capitalizeFirstLetter(string)).join(", ");
  };

  /**
   * Renders a modal dialog for confirming asset return
   * @param {Object} params - The parameters object
   * @param {() => void} params.close - Function to close the modal
   * @returns {JSX.Element} The rendered modal component
   */
  const renderReturnModal = ({ close }: { close: () => void }) => {
    const handleReturnConfirm = async () => {
      await returnAsset();
    };

    return (
      <SubmitReturnModal
        close={close}
        productName={props.item.name}
        onConfirm={handleReturnConfirm}
        isLoading={isLoadingAssetReturnStatus}
      />
    );
  };

  /**
   * Renders a button that triggers the return asset modal
   * Uses WithModal component to handle modal display logic
   * @returns {JSX.Element} A button wrapped in WithModal component
   */
  const displayReturnButton = () => {
    return (
      <WithModal renderModal={renderReturnModal}>
        {({ open }: { open: () => void }) => (
          <div className="return-button-container">
            <Button
              variant={ButtonVariant.Secondary}
              onClick={open}
              data-cy="return-asset-button"
              data-testid="return-asset-button"
              icon={isLoadingAssetReturnStatus ? <Spinner size={SpinnerSize.Small} /> : undefined}
              disabled={isLoadingAssetReturnStatus || !canReturn}
            >
              {getString("licenseReturn.returnButton")}
            </Button>
          </div>
        )}
      </WithModal>
    );
  };

  const renderCFM = () => {
    const module =
      props.assetType === ItemTypes.HARDWARE ? CFMModuleName.hardwareProductDetails : CFMModuleName.productDetails;

    return (
      <View>
        <Text color={"neutral90"} fontSize={"T200"} fontWeight={"regular"}>
          {parse(props.item.description)}
        </Text>
        <Hr color="neutral30" />
        <CustomerFeedback
          moduleName={module}
          alignment={CustomerFeedbackAlignment.Left}
          position={CustomerFeedbackIconPosition.Left}
        />
      </View>
    );
  };

  const renderBreadcrumbs = () => {
    const parentItemName = props.item.parentName ?? props.item.name;
    return (
      <PageMainContainer>
        <Breadcrumbs
          crumbs={[
            { title: "Home", url: "/" },
            { title: capitalize(props.assetType), url: breadCrumbsMapping[props.assetType] },
            { title: parentItemName },
          ]}
          onCrumbClick={handleCrumbClick}
        />
      </PageMainContainer>
    );
  };

  const renderProductImage = () => (
    <Col className="pdp-column-left">
      <div className="product-img-wrapper">
        <img
          data-details-logo="details-logo"
          className="product-img"
          src={props.item.logoUrl.trim() !== "" ? props.item.logoUrl : NoLogoImage}
          alt={`${props.item.name} logo`}
        />
      </div>
    </Col>
  );

  const renderProductTitle = () => {
    const parentItemName = props.item.parentName ?? props.item.name;
    return (
      <Row gridGap={"8px"}>
        <Text fontSize="T200" className="product-title" data-cy="product-title">
          {parentItemName}
        </Text>
        {isFoundational && <FoundationalChip />}
      </Row>
    );
  };

  const renderRatingsAndReviews = () => {
    return (
      <Row style={{ marginTop: "8px" }}>
        <StarAverageRating
          size={StarAverageRatingSize.Medium}
          averageRating={props.item.feedback?.averageRating ?? 0}
        />
        <Button
          variant={ButtonVariant.Tertiary}
          icon={<IconMessageSmall />}
          onClick={() => setOpenFlyout(true)}
          dataTestId="submit-review-button"
        >
          Submit review
        </Button>
      </Row>
    );
  };

  const renderShortDescription = () =>
    props.item?.shortDescription && (
      <div className="product-short-details" data-cy="product-short-details">
        <Text data-details-description="details-description" fontSize="T100" className="product-description">
          {props.item.shortDescription}
        </Text>
      </div>
    );

  const renderChips = () => {
    const chips: ReactElement[] = [];

    if (props.item.isAccessible) {
      chips.push(
        <Chip
          icon={<IconAccessibilityPersonCircleExtraSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          key="accesible"
        >
          Accessible
        </Chip>
      );
    }

    // category chips
    forEach(props.item.categories, (category) => {
      chips.push(
        <Chip
          icon={<IconBookmarkFillExtraSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          key={`category-${category}`}
        >
          {category}
        </Chip>
      );
    });

    // os chips
    forEach(props.item.os, (os) => {
      chips.push(
        <Chip
          icon={<IconDesktopExtraSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          key={`os-${os}`}
        >
          {os}
        </Chip>
      );
    });

    return (
      <Row gridGap="S200" style={{ marginTop: "24px", marginBottom: "24px" }}>
        {chips}
      </Row>
    );
  };

  const renderProductProperties = () => {
    return (
      <Row columnGap={60} gridGap={0} justifyContent="space-between" style={{ marginBottom: "24px" }}>
        <Text className="product-property">
          <label data-cy="delivery-time-title">{getString("details.deliveryTimeframe")}</label>
          <br />
          <EtaStatusIndicator etaDays={props.item.etaDays} hasStock={props.item.hasStock} size={IconSize.Small} />
        </Text>

        <Text className="product-property">
          <label data-cy="data-classification-title">{getString("details.dataClassification")}</label>
          <br />
          <strong>{props.item.dataClassification ?? "N/A"}</strong>
        </Text>

        <Text className="product-property">
          <label data-cy="security-status-title">{getString("details.securityStatus")}</label>
          <br />
          <strong>{props.item.securityStatus ?? "N/A"}</strong>
        </Text>

        <Text className="product-property">
          <label data-cy="version-title">{getString("details.version")}</label>
          <br />
          <strong>{props.item.version ?? "N/A"}</strong>
        </Text>
      </Row>
    );
  };

  const renderVersionSelector = () => {
    if (props.versions.length > 0) {
      return (
        <Col data-testid="select-version-component" style={{ width: "100%" }}>
          <SelectChildSku
            itemChildVersions={props.versions}
            onChange={handleChangeVersion}
            selectedVersion={selectedVersion}
            autoSelectFirstVersion={false}
            isLegacy={false}
          />
        </Col>
      );
    }
  };

  const renderLicenseSelector = () => {
    if (isPriceOverwritten) {
      return (
        <Col data-testid="select-license-component">
          <LicenseSelect
            updateSelectedLicense={updateSelectedLicense}
            selectedLicense={licenseSelection}
            licenses={licenses}
          />
        </Col>
      );
    }
  };

  const renderOrderSection = ({ open }: { open: () => void }) => (
    <Col className="pdp-column-right" alignItems="flex-end">
      {props.defaultVersion && (
        <OrderNow
          orderWorkflow={props.item.orderWorkflow}
          externalButtonText={props.item.externalButtonText}
          itemType={props.assetType}
          orderNowHandler={open}
          goToCheckoutHandler={goToCheckoutPage}
          orderRedirectUrl={props.item.orderRedirectUrl || ""}
          moreDetailsUrl={props.item.moreDetailsUrl || ""}
          amount={amount}
          status={props.defaultVersion?.status.toLocaleLowerCase()}
          currency={props.defaultVersion?.currency}
          hasReachedCap={limitCap.isCapReached}
          isLoading={isLoadingAssetReturnStatus || loadingOrders || isLicenseCapLoading}
          activeOrder={!!activeOrder}
          hasReturnInProcess={assetStatus?.returnStatus?.inProgress}
          isLegacy={false}
          selectVersionComponent={renderVersionSelector()}
          selectLicenseComponent={renderLicenseSelector()}
        />
      )}

      {assetStatus?.hasLicense && displayReturnButton()}
    </Col>
  );

  const renderProductDetailsSection = ({ open }: { open: () => void }) => (
    <PageMainContainer className="product-details" paddingBottom="S500">
      <View>
        <Container paddingBottom="S300" paddingTop="S300">
          <ProductDetailsBanners
            limitCap={limitCap}
            assetStatus={assetStatus}
            isOsCompatible={isOsCompatible}
            hasOrderedItem={hasOrderedItem}
            productName={props.item.name}
          />
        </Container>
        <Row key={props.item.taxonomyId} gridGap="S700" alignItems="flex-start" className="product-description-box">
          {renderProductImage()}
          <Col className="pdp-column-center">
            {renderProductTitle()}
            {renderRatingsAndReviews()}
            {renderShortDescription()}
            {renderChips()}
            <hr />
            {renderProductProperties()}
          </Col>
          {renderOrderSection({ open })}
        </Row>
      </View>
    </PageMainContainer>
  );

  const renderAboutProductSection = () => (
    <PageMainContainer className="about-product-view" paddingBottom="S500" paddingTop="S500" paddingHorizontal="S300">
      <View style={{ marginBottom: "32px" }}>
        <TabBar {...tabBarProps} />
      </View>

      <TabSwitcher {...tabSwitcherProps}>
        <TabPanel value="About the product">
          <View>
            <Row
              key={props.item.taxonomyId}
              gridGap="S700"
              alignItems="center"
              className="product-description-box"
              data-cy="product-description-box"
            >
              {props.item.description && renderCFM()}
            </Row>
          </View>
        </TabPanel>

        <TabPanel value="Resources">
          <Text>Resources content goes here</Text> {/* PENDING DATA FROM CATALOG */}
        </TabPanel>

        <TabPanel value="Support">
          <Text>Support content goes here</Text> {/* PENDING DATA FROM CATALOG */}
        </TabPanel>
      </TabSwitcher>
    </PageMainContainer>
  );

  return (
    <WithModal renderModal={renderModal}>
      {({ open }: { open: () => void }) => (
        <>
          {renderBreadcrumbs()}
          {renderProductDetailsSection({ open })}
          {renderAboutProductSection()}
          {renderFlyout()}
        </>
      )}
    </WithModal>
  );
};
